import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import Header from '../components/Layout/Header/Header'
import MainContent from '../components/Layout/MainContent/MainContent'
import {
    GALLERY_REDUCER_NAMESPACE,
    NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE,
} from '../redux/reducers/activity-creation'
import { LuxIcon, LuxInputSwitch, LuxTextInput, LuxButton, LuxBreadcrumb } from '@lux/wardrobe-lib'
import NewAlbumAccordion from '../components/Gallery/NewAlbumAccordion'
import '../components/Gallery/Gallery.scss'
import './NewActivity.scss'
import './NewAlbum.scss'
import { getAlbumDetails, setAlbumDetails } from '../redux/actions/albums'
import {
    ability,
    Album,
    layoutActions,
    SmartlyActionItems,
    SmartlyActionTypes,
} from '@Techedge/digitalportal-shared'
import { ProjectsService } from '../services/ProjectsService'
import SelectProjectDialog from '../components/Gallery/SelectProjectDialog'
import AddedPhotosAccordion from '../components/Gallery/AddedPhotosAccordion'

const NewAlbum = (props: {
    id: string
    ctaTemplate: any
    albumDetails: Album
    setAlbumDetails: (a: Album) => void
    getAlbumDetails: (id: string) => void
    closeModal: () => void
    goToProject: (id: string) => void
    isEdit: boolean
}) => {
    const {
        ctaTemplate,
        albumDetails,
        setAlbumDetails,
        closeModal,
        id,
        getAlbumDetails,
        goToProject,
    } = props
    const [isOpen, setIsOpen] = useState(false)
    const [isEdit, setIsEdit] = useState(true)

    const intl = useIntl()

    const componentClass = 'smart-new-album smart-modals-new-activity'

    const activityBreadcrumb = {
        id: '0',
        label: intl.formatMessage({
            id: 'ph2.modals.new.activity.breadcrumbs.album',
            defaultMessage: 'Album',
        }),
        url: '/activity',
        className: 'activity selectable',
    }

    const createBreadcrumb = {
        id: '1',
        label:
            albumDetails?.name ||
            intl.formatMessage({
                id: 'modals.new.activity.breadcrumbs.create',
                defaultMessage: 'Create',
            }),
        disabled: true,
    }

    useEffect(() => {
        if (id) {
            getAlbumDetails(id)
            setIsEdit(props.isEdit)
        }
    }, [id])

    useEffect(() => {
        if (id && albumDetails.filters?.projectIds) {
            if (albumDetails.filters.projectIds === '*') {
            } else if (
                albumDetails.filters.projectIds.length > 0 &&
                !albumDetails.filters.selectedProjects
            ) {
                ProjectsService.GetProjectNames(albumDetails.filters.projectIds).then((res) => {
                    setAlbumDetails({
                        ...albumDetails,
                        filters: { ...albumDetails.filters, selectedProjects: res.data.activities },
                    })
                })
            }
        }
    }, [albumDetails])

    const onChangeField = (val, field) => {
        setAlbumDetails({ ...albumDetails, [field]: val })
    }

    return (
        <div className="smart-modals-fullscreen">
            <div className={componentClass}>
                <div className={`${componentClass}__header`}>
                    <Header onClose={closeModal}>
                        <LuxBreadcrumb pathArray={[activityBreadcrumb, createBreadcrumb]} />
                        <h1 className={`${componentClass}__header-mobile`}>
                            {albumDetails?.name ||
                                intl.formatMessage({
                                    id: 'ph2.modals.new.album',
                                    defaultMessage: 'Create a New Album',
                                })}
                        </h1>
                    </Header>
                </div>
                <div className={`${componentClass}__main`}>
                    <div className={`${componentClass}__content pt-5 md:pt-0 w-100`}>
                        <MainContent
                            footer={isEdit ? ctaTemplate : undefined}
                            classes="new-album-main-content">
                            <div className="mt-5 mx-3">
                                {isEdit && (
                                    <>
                                        <LuxTextInput
                                            value={albumDetails?.name}
                                            onChangeCallback={(val) => onChangeField(val, 'name')}
                                            label={intl.formatMessage({
                                                id: 'ph2.modals.album.album_name',
                                                defaultMessage: 'Album name',
                                            })}
                                            mandatory
                                            containerClasses="mb-4"
                                            disabled={!isEdit}
                                        />
                                        {ability.can(
                                            SmartlyActionTypes.CREATE,
                                            SmartlyActionItems.ALBUM,
                                            'special_collection'
                                        ) && (
                                            <>
                                                <LuxInputSwitch
                                                    label={intl.formatMessage({
                                                        id: 'ph2.modals.album.special_collection',
                                                        defaultMessage: 'Special collection',
                                                    })}
                                                    value={albumDetails?.specialCollection}
                                                    onChangeCallback={(val) =>
                                                        onChangeField(val, 'specialCollection')
                                                    }
                                                />
                                                <div className="mt-2 flex align-items-center">
                                                    <LuxIcon icon="info" classes="mr-2" />
                                                    <span>
                                                        {intl.formatMessage({
                                                            id: 'ph2.modals.album.special_collection_info',
                                                            defaultMessage:
                                                                'This album collection is visible on PicStore.',
                                                        })}
                                                    </span>
                                                </div>
                                            </>
                                        )}

                                        <div className="form-label">
                                            {intl.formatMessage({
                                                id: 'ph2.modals.album.manual_photo_selection',
                                                defaultMessage: 'Manual Photo Selection',
                                            })}
                                        </div>
                                        <LuxButton
                                            text={intl.formatMessage({
                                                id: 'ph2.gallery.add.photo',
                                                defaultMessage: 'Add photo',
                                            })}
                                            variant="icon-badge"
                                            iconLeft="plus"
                                            onClick={() => setIsOpen(true)}
                                        />
                                    </>
                                )}
                            </div>
                            {albumDetails.manualAttachments?.length > 0 && isEdit && (
                                <AddedPhotosAccordion
                                    addedPhotos={albumDetails.manualAttachments}
                                />
                            )}
                            <div className="form-label mx-3 mb-3">
                                {isEdit
                                    ? intl.formatMessage({
                                          id: 'ph2.modals.album.select_filtering',
                                          defaultMessage: 'Select by filtering',
                                      })
                                    : intl.formatMessage({
                                          id: 'ph2.modals.album_filters',
                                          defaultMessage: 'Album filters',
                                      })}
                            </div>
                            <NewAlbumAccordion
                                albumDetails={albumDetails}
                                setAlbumDetails={setAlbumDetails}
                                disabled={!isEdit}
                            />
                        </MainContent>
                    </div>
                </div>
            </div>
            <SelectProjectDialog
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                onSelectProject={(id: string) => {
                    goToProject(id)
                }}
            />
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    albumDetails: state[GALLERY_REDUCER_NAMESPACE]?.newAlbum || new Album(),
    ctaTemplate: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].ctaTemplate,
    id: state.layoutReducer.modals.props?.id,
    isEdit: state.layoutReducer.modals.props?.isEdit,
})

const mapDispatchToProps = (dispatch: any) => ({
    setAlbumDetails: (data: any) => dispatch(setAlbumDetails(data)),
    getAlbumDetails: (id: string) => dispatch(getAlbumDetails(id)),
    closeModal: () => {
        dispatch(setAlbumDetails(new Album()))
        dispatch(
            layoutActions.setModals({
                type: null,
                props: { id: null, isEdit: false, selectedProject: null, isPreview: null },
            })
        )
    },
    goToProject: (id: string) => {
        dispatch(
            layoutActions.setModals({
                type: null,
                props: { selectedProject: id, isEdit: true },
            })
        )
    },
})

export default connect(mapStateToProps, mapDispatchToProps)(NewAlbum)
